import { ReactNode, useCallback, useMemo, useState } from "react";
import { AdFormat } from "../api/contracts/site-config";
import { URL_PARAMS } from "../utils/urlParams";
import { landingAdFormats, resolveAdFormat } from "./adFormatSelection";
import { useConfiguration } from "./ConfigurationProvider";
import { AdFormatContext, type AdFormatState } from "./adFormatContext";

/**
 * Reads `?module=` once the configuration resolves, and keeps it in the URL as
 * the guest switches. A value that is not one of the site's enabled formats is
 * ignored rather than rejected — a stale shared link should still open the
 * site, the same way a bad date param falls back instead of erroring.
 *
 * Sits above HeaderProvider so the header can consume the selected format
 * without the provider consuming its own context.
 */
export const AdFormatProvider = ({ children }: { children: ReactNode }) => {
  const { configuration } = useConfiguration();
  const options = useMemo(
    () => landingAdFormats(configuration?.adFormats ?? [], configuration?.featureFlags ?? []),
    [configuration],
  );
  const siteModule = configuration?.module;

  const [selected, setSelected] = useState<AdFormat | null>(null);

  const urlFormat = useMemo(() => {
    if (options.length === 0) return null;
    const raw = new URLSearchParams(window.location.search).get(URL_PARAMS.MODULE);
    return options.some((option) => option.module === raw) ? (raw as AdFormat) : null;
    // Re-read only when the offered set changes: the guest's own clicks go
    // through `selected`, which takes precedence below.
  }, [options]);

  const selectFormat = useCallback((module: AdFormat) => {
    setSelected(module);

    const url = new URL(window.location.href);
    url.searchParams.set(URL_PARAMS.MODULE, module);
    window.history.replaceState({}, "", url.toString());
  }, []);

  const value = useMemo<AdFormatState>(
    () => ({
      format: resolveAdFormat(selected, urlFormat, siteModule, options),
      options,
      selectFormat,
    }),
    [selected, urlFormat, siteModule, options, selectFormat],
  );

  return <AdFormatContext.Provider value={value}>{children}</AdFormatContext.Provider>;
};
